// src/Portfolio.js
import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "./supabaseClient";

const tableStyle = {
  borderCollapse: "collapse",
  width: "100%",
  fontFamily: "system-ui, -apple-system, sans-serif",
  minWidth: "650px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  borderRadius: "8px",
  overflow: "hidden",
};

const thTdStyle = {
  border: "1px solid #e5e7eb",
  padding: "10px 14px",
  textAlign: "left",
};

const thStyle = {
  backgroundColor: "#f9fafb",
  fontWeight: "600",
  color: "#374151",
};

const summaryCardStyle = {
  background: "#ffffff",
  padding: "1rem 1.25rem",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
};

const Portfolio = ({ teams = [], cash = 0 }) => {
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(true);

  // Safely resolve team identifier
  const getTeamKey = (t) => t.id || t.name;

  // Helper to extract logo across all naming variations & upgrade http->https
  const getTeamLogo = (t) => {
    if (!t) return "";
    let logo = t.logo_url || t.logoUrl || t.logo || t.logoURL || "";
    if (logo.startsWith("http://")) {
      logo = logo.replace("http://", "https://");
    }
    return logo;
  };

  // Listen for auth state initialization & fetch transactions from Supabase
  useEffect(() => {
    const fetchTransactions = async (userId) => {
      setLoadingTx(true);
      try {
        const { data, error } = await supabase
          .from("transactions")
          .select("*")
          .eq("user_id", userId)
          .order("timestamp", { ascending: false });

        if (error) throw error;
        setTransactions(data || []);
      } catch (error) {
        console.error("Error fetching transaction history:", error);
      } finally {
        setLoadingTx(false);
      }
    };

    // Get current logged-in user on mount
    const initAuthAndFetch = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        await fetchTransactions(user.id);
      } else {
        setTransactions([]);
        setLoadingTx(false);
      }
    };

    initAuthAndFetch();

    // Subscribe to auth state changes (login/logout/token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        fetchTransactions(session.user.id);
      } else {
        setTransactions([]);
        setLoadingTx(false);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Calculate Average Purchase Price per team from transaction history
  const averageCostMap = useMemo(() => {
    const map = {};
    // Sort transactions chronologically (oldest first) to compute correct running cost basis
    const sortedTx = [...transactions].sort(
      (a, b) => new Date(a.timestamp || a.created_at || 0) - new Date(b.timestamp || b.created_at || 0)
    );

    sortedTx.forEach((tx) => {
      const teamKey = tx.teamId || tx.team_id;
      const teamName = tx.teamName || tx.team_name;

      // Register keys for robust lookup
      const keys = [teamKey, teamName].filter(Boolean);
      
      keys.forEach((k) => {
        if (!map[k]) {
          map[k] = { shares: 0, totalCost: 0 };
        }
      });

      const qty = Number(tx.quantity || 0);
      const price = Number(tx.price || 0);

      if (tx.type === "BUY") {
        keys.forEach((k) => {
          map[k].shares += qty;
          map[k].totalCost += qty * price;
        });
      } else if (tx.type === "SELL") {
        keys.forEach((k) => {
          if (map[k].shares > 0) {
            const avg = map[k].totalCost / map[k].shares;
            map[k].shares = Math.max(0, map[k].shares - qty);
            map[k].totalCost = map[k].shares * avg;
          }
        });
      }
    });

    const avgPrices = {};
    Object.keys(map).forEach((k) => {
      const data = map[k];
      avgPrices[k] = data.shares > 0 ? data.totalCost / data.shares : 0;
    });

    return avgPrices;
  }, [transactions]);

  // Filter for teams where the user owns at least 1 share
  const ownedPositions = useMemo(() => {
    return teams.filter((team) => (team.shares || 0) > 0);
  }, [teams]);

  // Derived Portfolio Value & Net Worth (Using sell_price for accurate immediate liquidation valuation)
  const totalPortfolioValue = useMemo(() => {
    return ownedPositions.reduce((acc, team) => {
      const liquidationPrice = team.sell_price ?? team.price ?? 0;
      return acc + (team.shares || 0) * liquidationPrice;
    }, 0);
  }, [ownedPositions]);

  const totalNetWorth = cash + totalPortfolioValue;

  return (
    <div
      style={{
        padding: "1.5rem",
        maxWidth: "1200px",
        margin: "0 auto",
        paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))",
      }}
    >
      <h2 style={{ marginBottom: "1.25rem" }}>My Portfolio & Holdings</h2>

      {/* Summary Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "1rem",
          marginBottom: "2rem",
        }}
      >
        <div style={summaryCardStyle}>
          <h4 style={{ margin: "0 0 4px 0", color: "#6b7280" }}>Cash Balance</h4>
          <p style={{ margin: 0, fontSize: "1.35rem", fontWeight: "bold" }}>
            ${cash.toFixed(2)}
          </p>
        </div>

        <div style={summaryCardStyle}>
          <h4 style={{ margin: "0 0 4px 0", color: "#6b7280" }}>Portfolio Value</h4>
          <p
            style={{
              margin: 0,
              fontSize: "1.35rem",
              fontWeight: "bold",
              color: "#2563eb",
            }}
          >
            ${totalPortfolioValue.toFixed(2)}
          </p>
        </div>

        <div style={summaryCardStyle}>
          <h4 style={{ margin: "0 0 4px 0", color: "#6b7280" }}>Total Net Worth</h4>
          <p
            style={{
              margin: 0,
              fontSize: "1.35rem",
              fontWeight: "bold",
              color: "#2b8a3e",
            }}
          >
            ${totalNetWorth.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Owned Positions Table */}
      <h3 style={{ marginBottom: "0.75rem" }}>Owned Positions</h3>
      {ownedPositions.length === 0 ? (
        <p
          style={{
            color: "#6b7280",
            marginBottom: "2rem",
            padding: "1.5rem",
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            border: "1px dashed #d1d5db",
          }}
        >
          You don't own any shares yet. Head over to the Market tab to start trading!
        </p>
      ) : (
        <div
          style={{
            width: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            marginBottom: "2.5rem",
          }}
        >
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thTdStyle, ...thStyle }}>Team</th>
                <th style={{ ...thTdStyle, ...thStyle }}>Shares</th>
                <th style={{ ...thTdStyle, ...thStyle }}>Avg Purchase Price</th>
                <th style={{ ...thTdStyle, ...thStyle }}>Sell Price</th>
                <th style={{ ...thTdStyle, ...thStyle }}>Total Value</th>
                <th style={{ ...thTdStyle, ...thStyle }}>Profit / Loss</th>
              </tr>
            </thead>
            <tbody>
              {ownedPositions.map((team) => {
                const key = getTeamKey(team);
                const logoUrl = getTeamLogo(team);
                
                // Lookup average purchase price using team id or name
                const avgBuyPrice = averageCostMap[team.id] ?? averageCostMap[team.name] ?? 0;
                const liquidationPrice = team.sell_price ?? team.price ?? 0;
                const positionValue = (team.shares || 0) * liquidationPrice;
                const totalCostBasis = (team.shares || 0) * avgBuyPrice;
                const profitLoss = positionValue - totalCostBasis;
                const profitLossPercent = totalCostBasis > 0 ? (profitLoss / totalCostBasis) * 100 : 0;

                return (
                  <tr key={key}>
                    <td style={thTdStyle}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt={team.name}
                            referrerPolicy="no-referrer"
                            style={{
                              width: "28px",
                              height: "28px",
                              objectFit: "contain",
                              flexShrink: 0,
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "28px",
                              height: "28px",
                              backgroundColor: "#e5e7eb",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.75rem",
                              color: "#6b7280",
                              fontWeight: "bold",
                              flexShrink: 0,
                            }}
                          >
                            {team.name ? team.name.charAt(0) : "?"}
                          </div>
                        )}
                        <strong>{team.name || key}</strong>
                      </div>
                    </td>
                    <td style={thTdStyle}>{team.shares}</td>
                    <td style={thTdStyle}>${avgBuyPrice.toFixed(2)}</td>
                    <td style={thTdStyle}>${liquidationPrice.toFixed(2)}</td>
                    <td style={{ ...thTdStyle, fontWeight: "bold" }}>
                      ${positionValue.toFixed(2)}
                    </td>
                    <td
                      style={{
                        ...thTdStyle,
                        fontWeight: "bold",
                        color: profitLoss >= 0 ? "#2b8a3e" : "#c92a2a",
                      }}
                    >
                      {profitLoss >= 0 ? "+" : ""}${profitLoss.toFixed(2)} ({profitLossPercent >= 0 ? "+" : ""}{profitLossPercent.toFixed(1)}%)
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Transaction History Table */}
      <h3 style={{ marginBottom: "0.75rem" }}>Transaction History</h3>
      {loadingTx ? (
        <p style={{ color: "#6b7280" }}>Loading trades...</p>
      ) : transactions.length === 0 ? (
        <p style={{ color: "#6b7280" }}>No trades executed yet.</p>
      ) : (
        <div
          style={{
            width: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thTdStyle, ...thStyle }}>Type</th>
                <th style={{ ...thTdStyle, ...thStyle }}>Team</th>
                <th style={{ ...thTdStyle, ...thStyle }}>Qty</th>
                <th style={{ ...thTdStyle, ...thStyle }}>Price</th>
                <th style={{ ...thTdStyle, ...thStyle }}>Total</th>
                <th style={{ ...thTdStyle, ...thStyle }}>Date & Time</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => {
                const rawTimestamp = tx.timestamp || tx.created_at;
                const txDate = rawTimestamp?.toDate
                  ? rawTimestamp.toDate()
                  : new Date(rawTimestamp || 0);

                const teamName = tx.teamName || tx.team_name || tx.teamId || tx.team_id;
                const matchedTeam = teams.find(
                  (t) => getTeamKey(t) === tx.teamId || getTeamKey(t) === tx.team_id || t.name === teamName
                );
                const logoUrl = getTeamLogo(matchedTeam);

                return (
                  <tr key={tx.id}>
                    <td
                      style={{
                        ...thTdStyle,
                        fontWeight: "bold",
                        color: tx.type === "BUY" ? "#2b8a3e" : "#c92a2a",
                      }}
                    >
                      {tx.type}
                    </td>
                    <td style={thTdStyle}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {logoUrl && (
                          <img
                            src={logoUrl}
                            alt={teamName}
                            referrerPolicy="no-referrer"
                            style={{
                              width: "20px",
                              height: "20px",
                              objectFit: "contain",
                              flexShrink: 0,
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <span>{teamName}</span>
                      </div>
                    </td>
                    <td style={thTdStyle}>{tx.quantity}</td>
                    <td style={thTdStyle}>${(tx.price || 0).toFixed(2)}</td>
                    <td style={thTdStyle}>${(tx.total || 0).toFixed(2)}</td>
                    <td style={{ ...thTdStyle, whiteSpace: "nowrap" }}>
                      {isNaN(txDate.getTime())
                        ? "N/A"
                        : txDate.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Portfolio;